import useToast from '@src/hooks/useToast';
import chatApi, {
  ChatMessage,
  ChatRoomDetail,
  ChatRoomSummary,
  isChatMock,
} from '@src/services/chat';
import { getCookie } from 'cookies-next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

/** 시스템 발신자 uuid (백엔드 ChatService.SYSTEM_SENDER 와 동일) */
const SYSTEM_SENDER = 'SYSTEM';
/** 방 열려있을 때 새 메시지 폴링 주기(ms). 에브리타임 쪽지처럼 비실시간. */
const POLL_INTERVAL = 4000;

type OpenChatTarget = {
  uuid?: string;
  writer?: { uuid?: string };
};

/**
 * 후원 상세 "채팅으로 도움주기" 진입.
 * 백엔드가 (사연, 두 참여자)로 방을 멱등 생성/조회한 뒤 그 방으로 이동한다.
 */
export const useOpenDonationChat = () => {
  const router = useRouter();
  const { showToast } = useToast();
  const [isOpening, setIsOpening] = useState(false);

  const openChat = useCallback(
    async (target: OpenChatTarget) => {
      if (isOpening) return;
      const donationUuid = target?.uuid;
      if (!donationUuid) {
        showToast('error', '채팅방을 열 수 없는 게시글입니다.');
        return;
      }
      if (!isChatMock() && !getCookie('X-AUTH-TOKEN')) {
        showToast('error', '로그인이 필요한 기능입니다.');
        return;
      }
      setIsOpening(true);
      try {
        const room = await chatApi.createOrGetRoom(donationUuid);
        router.push(`/chat/${encodeURIComponent(room.uuid)}`);
      } catch (e) {
        showToast(
          'error',
          '채팅방을 열 수 없습니다. 잠시 후 다시 시도해주세요.'
        );
      } finally {
        setIsOpening(false);
      }
    },
    [isOpening, router, showToast]
  );

  return { openChat, isOpening };
};

type ChatRoomState = {
  room: ChatRoomDetail | null;
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
};

/** 메시지가 "내가 보낸 것"인지 — 1:1 방이라 상대·시스템이 아니면 나. */
export const isMine = (m: ChatMessage, opponentUuid: string) =>
  m.senderUuid !== opponentUuid && m.senderUuid !== SYSTEM_SENDER;

/**
 * 채팅방 하나의 메시지 목록 + 폴링 수신.
 * 백엔드 폴링 커서(afterId)로 마지막 메시지 이후만 가져온다.
 */
export const useChatRoom = (roomUuid: string) => {
  const [state, setState] = useState<ChatRoomState>({
    room: null,
    messages: [],
    isLoading: true,
    error: null,
  });
  const [isSending, setIsSending] = useState(false);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  // 폴링 콜백이 항상 최신 lastId를 보도록 ref로 유지
  const lastIdRef = useRef<number>(0);

  // 초기 로드: 방 상세 + 최신 메시지
  useEffect(() => {
    if (!roomUuid) return;
    if (!isChatMock() && !getCookie('X-AUTH-TOKEN')) {
      setState((p) => ({ ...p, isLoading: false, error: 'UNAUTHORIZED' }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [room, messages] = await Promise.all([
          chatApi.getRoom(roomUuid),
          chatApi.getMessages(roomUuid, { size: 30 }),
        ]);
        if (cancelled) return;
        lastIdRef.current = messages.length
          ? messages[messages.length - 1].id
          : 0;
        setState({ room, messages, isLoading: false, error: null });
        chatApi.markRead(roomUuid).catch(() => undefined);
      } catch (e) {
        if (!cancelled) {
          setState((p) => ({
            ...p,
            isLoading: false,
            error: '채팅방을 불러오지 못했습니다.',
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roomUuid]);

  // 폴링: afterId 이후 새 메시지 + 방 상세 갱신(상대 읽음 시각)
  useEffect(() => {
    if (!roomUuid || state.error) return;
    let stopped = false;
    const tick = async () => {
      if (stopped || document.hidden) return;
      try {
        const [fresh, room] = await Promise.all([
          chatApi.getMessages(roomUuid, { afterId: lastIdRef.current }),
          chatApi.getRoom(roomUuid),
        ]);
        if (stopped) return;
        if (fresh.length) {
          lastIdRef.current = fresh[fresh.length - 1].id;
          chatApi.markRead(roomUuid).catch(() => undefined);
        }
        setState((prev) => {
          const existing = new Set(prev.messages.map((m) => m.id));
          const added = fresh.filter((m) => !existing.has(m.id));
          return {
            ...prev,
            room: room ?? prev.room,
            messages: added.length
              ? [...prev.messages, ...added]
              : prev.messages,
          };
        });
      } catch (e) {
        // 일시적 실패는 조용히 넘기고 다음 주기에 재시도
      }
    };
    const timer = setInterval(tick, POLL_INTERVAL);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [roomUuid, state.error]);

  const appendSent = useCallback((sent: ChatMessage) => {
    lastIdRef.current = Math.max(lastIdRef.current, sent.id);
    setState((prev) =>
      prev.messages.some((m) => m.id === sent.id)
        ? prev
        : { ...prev, messages: [...prev.messages, sent] }
    );
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return false;
      setIsSending(true);
      try {
        const sent = await chatApi.sendMessage(
          roomUuid,
          trimmed,
          replyTo?.id ?? null
        );
        appendSent(sent);
        setReplyTo(null);
        return true;
      } catch (e) {
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [roomUuid, isSending, replyTo, appendSent]
  );

  const sendImage = useCallback(
    async (file: File) => {
      if (isSending) return false;
      setIsSending(true);
      try {
        const sent = await chatApi.sendImage(roomUuid, file);
        appendSent(sent);
        return true;
      } catch (e) {
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [roomUuid, isSending, appendSent]
  );

  const reportRoom = useCallback(
    (reason?: string) => chatApi.reportRoom(roomUuid, reason),
    [roomUuid]
  );
  const blockOpponent = useCallback(
    () => chatApi.blockOpponent(roomUuid),
    [roomUuid]
  );

  return {
    ...state,
    sendMessage,
    sendImage,
    reportRoom,
    blockOpponent,
    isSending,
    replyTo,
    setReplyTo,
  };
};

/** 헤더 배지용 안읽음 총합 (로그인 시에만, 주기적 폴링) */
export const useChatUnread = (enabled = true) => {
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    if (!enabled || (!isChatMock() && !getCookie('X-AUTH-TOKEN'))) {
      setUnread(0);
      return;
    }
    try {
      setUnread(await chatApi.getUnreadCount());
    } catch (e) {
      // 조용히 무시 (다음 주기에 재시도)
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    load();
    // 방을 열어 읽음 처리하면 배지가 곧 사라져야 하므로 너무 길게 두지 않는다.
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, 8000);
    return () => clearInterval(timer);
  }, [enabled, load]);

  return { unread, reload: load };
};

/** 쪽지함 목록 */
/**
 * 쪽지함 목록.
 *
 * activeRoomUuid를 넘기면 방이 바뀔 때마다 목록을 다시 불러온다.
 * 2분할 레이아웃에서는 사이드바가 언마운트되지 않아, 방을 열어 읽음 처리(markRead)가
 * 되어도 목록을 새로 안 받으면 안읽음 뱃지가 그대로 남는다.
 * 상대가 보낸 새 메시지도 반영되도록 주기적으로도 갱신한다.
 */
export const useChatRooms = (activeRoomUuid?: string) => {
  const [rooms, setRooms] = useState<ChatRoomSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!isChatMock() && !getCookie('X-AUTH-TOKEN')) {
      setError('UNAUTHORIZED');
      setIsLoading(false);
      return;
    }
    try {
      const data = await chatApi.getRooms();
      setRooms(data);
      setError(null);
    } catch (e) {
      setError('쪽지함을 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 최초 + 방 전환 시. 방을 열면 markRead가 먼저 나가므로 약간 늦춰 최신 안읽음을 받는다.
  useEffect(() => {
    load();
    if (!activeRoomUuid) return;
    const t = setTimeout(load, 1200);
    return () => clearTimeout(t);
  }, [load, activeRoomUuid]);

  // 열려 있는 동안 주기적 갱신(상대 새 메시지 / 읽음 반영)
  useEffect(() => {
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, POLL_INTERVAL * 2);
    return () => clearInterval(timer);
  }, [load]);

  return { rooms, isLoading, error, reload: load };
};
